import {
	type AgentStatus,
	getAgentInboundAnsweringPolicy,
	type InboundAnsweringPolicy,
} from "@repo/common";
import {
	createAgent,
	deleteAgent,
	updateAgent,
	updatePhoneNumber,
} from "@repo/ui";
import { getFirestore } from "firebase/firestore";
import {
	Check,
	ChevronsUpDown,
	Pencil,
	Plus,
	Search,
	Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import type { Agent } from "../types";
import { TypedDeleteConfirmationDialog } from "./TypedDeleteConfirmationDialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "./ui/command";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";
import { cn } from "./ui/utils";

interface AgentsPageProps {
	onNavigateToAgent: (agentId: string) => void;
}

export function AgentsPage({ onNavigateToAgent }: AgentsPageProps) {
	const { agents, companies, services, phoneNumbers } = useData();
	const db = getFirestore();
	const [searchTerm, setSearchTerm] = useState("");
	const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

	// Multi-select state
	const [selectedPhoneIds, setSelectedPhoneIds] = useState<string[]>([]);
	const [openCombobox, setOpenCombobox] = useState(false);

	const [formData, setFormData] = useState({
		companyId: "",
		serviceId: "",
		status: "Draft" as AgentStatus,
		inboundAnsweringPolicy: "AI_FIRST" as InboundAnsweringPolicy,
	});

	const filteredAgents = useMemo(() => {
		if (!searchTerm) return agents;

		return agents.filter((agent) => {
			const company = companies.find((c) => c.id === agent.companyId);
			const service = services.find((s) => s.id === agent.serviceId);
			const agentPhoneNumbers = phoneNumbers.filter(
				(p) => p.agentId === agent.id,
			);
			const hasMatchingPhone = agentPhoneNumbers.some((p) =>
				p.number.includes(searchTerm),
			);

			return (
				agent.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				agent.phoneNumbers.some((phone) =>
					phone.toLowerCase().includes(searchTerm.toLowerCase()),
				) ||
				hasMatchingPhone ||
				company?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				service?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				agent.status.toLowerCase().includes(searchTerm.toLowerCase())
			);
		});
	}, [agents, companies, services, phoneNumbers, searchTerm]);

	const formatDate = (dateString: Date | string | null | undefined) => {
		if (!dateString) return "Never";
		return new Date(dateString ?? "").toLocaleString();
	};

	const handleAddClick = () => {
		setFormData({
			companyId: "",
			serviceId: "",
			status: "Draft",
			inboundAnsweringPolicy: "AI_FIRST",
		});
		setSelectedPhoneIds([]);
		setIsAddDialogOpen(true);
	};

	const handleAddSubmit = async () => {
		// Validate form
		if (!formData.companyId) {
			toast.error("Please fill in all required fields");
			return;
		}

		try {
			// Find primary number for backward compatibility (first selected)
			const primaryPhone =
				selectedPhoneIds.length > 0
					? phoneNumbers.find((p) => p.id === selectedPhoneIds[0])?.number
					: "";

			const newAgentId = await createAgent(db, {
				...formData,
				name: `Agent ${Date.now()}`,
				phoneNumbers: primaryPhone ? [primaryPhone] : [],
				version: 1,
				serviceId: formData.serviceId || null,
			});

			// Assign selected phone numbers to this new agent
			await Promise.all(
				selectedPhoneIds.map((phoneId) =>
					updatePhoneNumber(db, phoneId, { agentId: newAgentId }),
				),
			);

			toast.success("Agent created successfully");

			setIsAddDialogOpen(false);
		} catch (error) {
			console.error("Failed to create agent", error);
			toast.error("Failed to create agent");
		}
	};

	const handleEditClick = (agent: Agent) => {
		setSelectedAgent(agent);
		setFormData({
			companyId: agent.companyId,
			serviceId: agent.serviceId || "",
			status: agent.status,
			inboundAnsweringPolicy: getAgentInboundAnsweringPolicy(agent),
		});

		// Initialize selected phones
		const associatedPhones = phoneNumbers
			.filter((p) => p.channel === "live" && p.agentId === agent.id)
			.map((p) => p.id)
			.filter((id): id is string => !!id);
		setSelectedPhoneIds(associatedPhones);

		setIsEditDialogOpen(true);
	};

	const handleEditSubmit = async () => {
		if (!selectedAgent || !selectedAgent.id) return;

		try {
			// 1. Update Agent Details
			// Determine primary phone number (legacy support)
			const primaryPhone =
				selectedPhoneIds.length > 0
					? phoneNumbers.find((p) => p.id === selectedPhoneIds[0])?.number
					: "";

			await updateAgent(db, selectedAgent.companyId, selectedAgent.id, {
				...formData,
				phoneNumbers: primaryPhone
					? [
							primaryPhone,
							...selectedPhoneIds
								.slice(1)
								.map((id) => phoneNumbers.find((p) => p.id === id)?.number)
								.filter((value): value is string => Boolean(value)),
						]
					: selectedPhoneIds
							.map((id) => phoneNumbers.find((p) => p.id === id)?.number)
							.filter((value): value is string => Boolean(value)),
			});

			// 2. Handle Phone Number Assignments
			const currentAssociatedPhones = phoneNumbers
				.filter((p) => p.channel === "live" && p.agentId === selectedAgent.id)
				.map((p) => p.id)
				.filter((id): id is string => !!id);

			const addedPhoneIds = selectedPhoneIds.filter(
				(id) => !currentAssociatedPhones.includes(id),
			);
			const removedPhoneIds = currentAssociatedPhones.filter(
				(id) => !selectedPhoneIds.includes(id),
			);

			await Promise.all([
				// Assign added numbers
				...addedPhoneIds.map((id) =>
					updatePhoneNumber(db, id, { agentId: selectedAgent.id }),
				),
				// Unassign removed numbers
				...removedPhoneIds.map((id) =>
					updatePhoneNumber(db, id, { agentId: null }),
				),
			]);

			toast.success(`Agent updated successfully`);
			setIsEditDialogOpen(false);
			setSelectedAgent(null);
		} catch (error) {
			console.error("Failed to update agent", error);
			toast.error("Failed to update agent");
		}
	};

	const handleDeleteClick = (agent: Agent) => {
		setSelectedAgent(agent);
		setIsDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!selectedAgent || !selectedAgent.id) return;

		try {
			// Also unassign phone numbers?
			// Usually good practice, but maybe let them keep the dead reference or handle it server side.
			// For now, let's just delete the agent. The phone numbers will still point to it (stale reference)
			// or we should clean them up.
			// Let's clean them up to be nice.
			const associatedPhones = phoneNumbers.filter(
				(p) => p.agentId === selectedAgent.id,
			);
			await Promise.all(
				associatedPhones.map((p) =>
					updatePhoneNumber(db, p.id, { agentId: null }),
				),
			);

			await deleteAgent(db, selectedAgent.companyId, selectedAgent.id);
			toast.success(`Agent "${selectedAgent.id}" deleted successfully`);
			setIsDeleteDialogOpen(false);
			setSelectedAgent(null);
		} catch (error) {
			console.error("Failed to delete agent", error);
			toast.error("Failed to delete agent");
		}
	};

	const handleServiceChange = (serviceId: string) => {
		const service = services.find((s) => s.id === serviceId);
		setFormData({
			...formData,
			serviceId: serviceId,
			companyId: service?.companyId || "",
		});
	};

	const getCompanyName = (companyId: string) => {
		const company = companies.find((c) => c.id === companyId);
		return company?.name || "Unknown";
	};

	const getServiceName = (serviceId: string | null | undefined) => {
		if (!serviceId) return <span className="text-slate-400">Unassigned</span>;
		const service = services.find((s) => s.id === serviceId);
		return service?.name || "Unknown";
	};

	const formatDeploymentRevision = (
		version: number | string | undefined,
	): string => {
		if (typeof version === "number" && Number.isFinite(version)) {
			return `v${version}`;
		}

		if (typeof version === "string" && version.trim()) {
			return version.trim();
		}

		return "Not deployed";
	};

	const renderRevisionCell = (agent: (typeof agents)[number]) => {
		const revisions = [
			{
				label: "Staging",
				value: formatDeploymentRevision(agent.stagingDeployment?.version),
			},
			{
				label: "Live",
				value: formatDeploymentRevision(agent.liveDeployment?.version),
			},
		];

		return (
			<div className="flex flex-col gap-1 text-sm">
				{revisions.map((revision) => {
					const isMissing = revision.value === "Not deployed";
					return (
						<div key={revision.label} className="flex items-center gap-2">
							<span className="w-14 shrink-0 text-slate-500">
								{revision.label}
							</span>
							<span
								className={cn(
									"font-mono",
									isMissing ? "text-slate-400" : "text-slate-700",
								)}
							>
								{revision.value}
							</span>
						</div>
					);
				})}
			</div>
		);
	};

	const renderPhoneNumberSelector = () => {
		// Filter available phone numbers:
		// 1. Unassigned numbers
		// 2. Numbers already assigned to THIS agent (if editing)
		// 3. Numbers assigned to other agents? Maybe warn or show them disabled?
		// Let's showing all numbers but indicating if they are taken.
		// Actually, common pattern is to only show free numbers + current agent's numbers.

		// For simplicity: Show all numbers. If taken by another, show (Assigned to X).

		const availableAndAssociated = phoneNumbers.filter(
			(phoneNumber) => phoneNumber.channel === "live",
		);

		return (
			<div className="space-y-2">
				<Label>Phone Numbers</Label>
				<Popover open={openCombobox} onOpenChange={setOpenCombobox}>
					<PopoverTrigger asChild>
						<Button
							variant="outline"
							role="combobox"
							aria-expanded={openCombobox}
							className="w-full justify-between h-auto min-h-[2.5rem] py-2"
						>
							<div className="flex flex-wrap gap-1">
								{selectedPhoneIds.length === 0 && "Select phone numbers..."}
								{selectedPhoneIds.map((id) => {
									const phone = phoneNumbers.find((p) => p.id === id);
									return (
										<Badge
											key={id}
											variant="secondary"
											className="mr-1"
											onClick={(e) => {
												e.stopPropagation();
												setSelectedPhoneIds((prev) =>
													prev.filter((pid) => pid !== id),
												);
											}}
										>
											{phone ? phone.number : id}
											<span className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer">
												✕
											</span>
										</Badge>
									);
								})}
							</div>
							<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
						</Button>
					</PopoverTrigger>
					<PopoverContent className="w-[300px] p-0">
						<Command>
							<CommandInput placeholder="Search phone number..." />
							<CommandList>
								<CommandEmpty>No phone number found.</CommandEmpty>
								<CommandGroup heading="Phone Numbers">
									{availableAndAssociated.map((phone) => {
										const phoneId = phone.id || "";
										const isSelected = selectedPhoneIds.includes(phoneId);
										const isAssignedToOther =
											phone.agentId && phone.agentId !== selectedAgent?.id;

										return (
											<CommandItem
												key={phoneId}
												value={phone.number}
												onSelect={() => {
													if (!phoneId) return;
													if (isSelected) {
														setSelectedPhoneIds((prev) =>
															prev.filter((id) => id !== phoneId),
														);
													} else {
														setSelectedPhoneIds((prev) => [...prev, phoneId]);
													}
												}}
												className={cn(isAssignedToOther && "opacity-50")}
											>
												<Check
													className={cn(
														"mr-2 h-4 w-4",
														isSelected ? "opacity-100" : "opacity-0",
													)}
												/>
												{phone.number}
												{isAssignedToOther && (
													<span className="ml-2 text-xs text-muted-foreground">
														(Taken)
													</span>
												)}
											</CommandItem>
										);
									})}
								</CommandGroup>
							</CommandList>
						</Command>
					</PopoverContent>
				</Popover>
			</div>
		);
	};

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-start">
				<div>
					<h1>Agents</h1>
					<p className="text-slate-600">
						Manage call agents and their configurations
					</p>
				</div>
				{/* Replaced Dialog trigger with onClick to correctly init state */}
				<Button onClick={handleAddClick}>
					<Plus className="mr-2 h-4 w-4" />
					Add Agent
				</Button>

				<Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
					<DialogContent className="admin-editor-dialog">
						<DialogHeader>
							<DialogTitle>Add New Agent</DialogTitle>
							<DialogDescription>Create a new call agent.</DialogDescription>
						</DialogHeader>
						<div className="admin-editor-body admin-editor-grid">
							{/* Multi-Select Phone Number */}
							<div className="admin-editor-span">
								{renderPhoneNumberSelector()}
							</div>

							<div className="space-y-2">
								<Label htmlFor="company">Company *</Label>
								<Select
									value={formData.companyId}
									onValueChange={(value: string) =>
										setFormData({ ...formData, companyId: value })
									}
								>
									<SelectTrigger id="company">
										<SelectValue placeholder="Select a company" />
									</SelectTrigger>
									<SelectContent>
										{companies.map((company) => (
											<SelectItem key={company.id} value={company.id}>
												{company.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label htmlFor="service">Service (Optional)</Label>
								<Select
									value={formData.serviceId || undefined}
									onValueChange={(value: string) =>
										setFormData({ ...formData, serviceId: value })
									}
								>
									<SelectTrigger id="service">
										<SelectValue placeholder="Select a service (optional)" />
									</SelectTrigger>
									<SelectContent>
										{services
											.filter((s) => s.companyId === formData.companyId)
											.map((service) => (
												<SelectItem key={service.id} value={service.id}>
													{service.name}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label htmlFor="status">Status *</Label>
								<Select
									value={formData.status}
									onValueChange={(value: AgentStatus) =>
										setFormData({ ...formData, status: value })
									}
								>
									<SelectTrigger id="status">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="Draft">Draft</SelectItem>
										<SelectItem value="Active">Active</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label htmlFor="answering-policy">Inbound answering</Label>
								<Select
									value={formData.inboundAnsweringPolicy}
									onValueChange={(value: InboundAnsweringPolicy) =>
										setFormData({ ...formData, inboundAnsweringPolicy: value })
									}
								>
									<SelectTrigger id="answering-policy">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="AI_FIRST">AI first</SelectItem>
										<SelectItem value="AI_ONLY">AI only</SelectItem>
										<SelectItem value="HUMAN_FIRST">Human first</SelectItem>
										<SelectItem value="HUMAN_ONLY">Human only</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setIsAddDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button onClick={handleAddSubmit}>Create Agent</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{/* Edit Dialog */}
			<Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
				<DialogContent className="admin-editor-dialog">
					<DialogHeader>
						<DialogTitle>Edit Agent</DialogTitle>
						<DialogDescription>
							Update agent details. ID and dates are read-only. Company updates
							based on selected service.
						</DialogDescription>
					</DialogHeader>
					<div className="admin-editor-body admin-editor-grid">
						<div className="space-y-2">
							<Label htmlFor="edit-agent-id">Agent ID</Label>
							<Input
								id="edit-agent-id"
								value={selectedAgent?.id || ""}
								disabled
								className="bg-slate-100 cursor-not-allowed"
							/>
						</div>
						{/* Multi-Select Phone Number */}
						<div className="admin-editor-span">
							{renderPhoneNumberSelector()}
						</div>

						<div className="space-y-2">
							<Label htmlFor="edit-service">Service</Label>
							<Select
								value={formData.serviceId || undefined}
								onValueChange={handleServiceChange}
							>
								<SelectTrigger id="edit-service">
									<SelectValue placeholder="Select a service (optional)" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="none">Unassigned</SelectItem>
									{services.map((service) => (
										<SelectItem key={service.id} value={service.id}>
											{service.name} ({getCompanyName(service.companyId)})
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-company">
								Company (Auto-updated from Service)
							</Label>
							<Input
								id="edit-company"
								value={getCompanyName(formData.companyId)}
								disabled
								className="bg-slate-100 cursor-not-allowed"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-status">Status</Label>
							<Select
								value={formData.status}
								onValueChange={(value: AgentStatus) =>
									setFormData({ ...formData, status: value })
								}
							>
								<SelectTrigger id="edit-status">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="Draft">Draft</SelectItem>
									<SelectItem value="Active">Active</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-answering-policy">Inbound answering</Label>
							<Select
								value={formData.inboundAnsweringPolicy}
								onValueChange={(value: InboundAnsweringPolicy) =>
									setFormData({ ...formData, inboundAnsweringPolicy: value })
								}
							>
								<SelectTrigger id="edit-answering-policy">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="AI_FIRST">AI first</SelectItem>
									<SelectItem value="AI_ONLY">AI only</SelectItem>
									<SelectItem value="HUMAN_FIRST">Human first</SelectItem>
									<SelectItem value="HUMAN_ONLY">Human only</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsEditDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button onClick={handleEditSubmit}>Save Changes</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<TypedDeleteConfirmationDialog
				open={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
				entityType="Agent"
				entityName={selectedAgent?.name ?? ""}
				consequences="This action cannot be undone. Any assigned phone numbers will become unassigned."
				onConfirm={handleDeleteConfirm}
			/>

			<Card>
				<CardHeader>
					<CardTitle>All Agents</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="mb-4">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
							<Input
								placeholder="Search by ID, name, phone number, company, service, status..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-10"
							/>
						</div>
					</div>

					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Agent ID</TableHead>
								<TableHead>Name</TableHead>
								<TableHead>Phone Numbers</TableHead>
								<TableHead>Company</TableHead>
								<TableHead>Service</TableHead>
								<TableHead>Revisions</TableHead>
								<TableHead>Last Call</TableHead>
								<TableHead>Created</TableHead>
								<TableHead>Updated</TableHead>
								<TableHead className="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredAgents.map((agent) => (
								<TableRow
									key={agent.id}
									className="cursor-pointer hover:bg-slate-50"
									onClick={() => onNavigateToAgent(agent.id)}
								>
									<TableCell>{agent.id}</TableCell>
									<TableCell>{agent.name}</TableCell>
									<TableCell>
										<div className="flex flex-col gap-1">
											{agent.phoneNumbers.length > 0 ? (
												agent.phoneNumbers.map((number) => (
													<div key={number} className="text-sm">
														{number}
													</div>
												))
											) : (
												<span className="text-slate-400 text-sm">
													No number
												</span>
											)}
										</div>
									</TableCell>
									<TableCell>{getCompanyName(agent.companyId)}</TableCell>
									<TableCell>{getServiceName(agent.serviceId)}</TableCell>
									<TableCell>{renderRevisionCell(agent)}</TableCell>
									<TableCell>{formatDate(agent.lastCall)}</TableCell>
									<TableCell>{formatDate(agent.createdAt)}</TableCell>
									<TableCell>{formatDate(agent.updatedAt)}</TableCell>
									<TableCell className="text-right">
										<div className="flex justify-end gap-2">
											<Button
												variant="ghost"
												size="sm"
												onClick={(e: React.MouseEvent) => {
													e.stopPropagation();
													handleEditClick(agent);
												}}
											>
												<Pencil className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="sm"
												onClick={(e: React.MouseEvent) => {
													e.stopPropagation();
													handleDeleteClick(agent);
												}}
											>
												<Trash2 className="h-4 w-4 text-red-600" />
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					{filteredAgents.length === 0 && (
						<div className="text-center py-8 text-slate-500">
							No agents found
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
